import React, { useEffect, useState, useContext } from 'react';
import { Card, Row, Col, Button, Table, Pagination, Spinner, Modal } from '@themesberg/react-bootstrap';
import { useTranslation } from "react-i18next";
import API from '../../utils/api';
import SingleUserAllClaims from './SingleUserAllClaims';
import { AuthContext } from '../../utils/AuthContext';
import deleteIcon from '../../assets/img/bin.png';
import trashIcon from '../../assets/img/trash.png'
export const AllUsersWithClaims = () => {
    const { t } = useTranslation();
    const ITEMS_PER_PAGE = 8;

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { impersonate } = useContext(AuthContext);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchUsersWithClaims = async () => {
        try {
            setLoading(true);
            const response = await API.get('/profile/users/with-claims');
            const allUsers = response.data.data || [];
            setUsers(allUsers);
            setTotalPages(Math.max(1, Math.ceil(allUsers.length / ITEMS_PER_PAGE)));
        } catch (err) {
            console.error('Failed to fetch users with claims:', err);
            setError(t("allUsersWithClaimsTranslations.alerts.deleteError"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsersWithClaims();
    }, []);

    const renderPaginationItems = () => {
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => setCurrentPage(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }
        return items;
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        setDeleteLoading(true); // show spinner immediately
        try {
            await API.delete(`/profile/delete-user/${userToDelete}`);
            const updatedUsers = users.filter(user => user.userId !== userToDelete);
            setUsers(updatedUsers);
            setDeleteSuccess(true);
            setDeleteError(null);

            const newTotalPages = Math.max(1, Math.ceil(updatedUsers.length / ITEMS_PER_PAGE));
            if (currentPage > newTotalPages) setCurrentPage(newTotalPages);

        } catch (err) {
            console.error("Failed to delete user:", err);
            setDeleteError(t("allUsersWithClaimsTranslations.alerts.deleteError"));
        } finally {
            setDeleteLoading(false); // hide spinner
        }
    };


    const handleImpersonate = async (userId) => {
        try {
            const response = await API.post(`/impersonate/${userId}`);
            const { token, client } = response.data;

            if (!token || !client) throw new Error("Invalid impersonation response");

            impersonate(token, client);
            window.location.href = "/client/dashboard-overview";
        } catch (err) {
            console.error("Failed to impersonate:", err);
            alert(t("allUsersWithClaimsTranslations.alerts.impersonateError"));
        }
    };

    const openPopup = (user) => {
        setSelectedUser(user);
        setShowPopup(true);
    };

    const closePopup = () => {
        setSelectedUser(null);
        setShowPopup(false);
    };

    if (loading) {
        return (
            <Card className="shadow-sm mt-4 p-4 text-center">
                <Spinner animation="border" />
                <p className="mt-2">{t("allUsersWithClaimsTranslations.pagination.loading")}</p>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="shadow-sm mt-4 p-4 text-center text-danger">
                <p>{error}</p>
            </Card>
        );
    }

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedUsers = users.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return (
        <Card border="light" className="saas-card mt-4">
            <Card.Header className="saas-card-header">
                <Row className="align-items-center">
                    <Col>
                        <h5 className="saas-heading">{t("allUsersWithClaimsTranslations.heading")}</h5>
                    </Col>
                </Row>
            </Card.Header>

            <Table responsive className="table-flush align-middle saas-table">
                <thead className="saas-table-head">
                    <tr>
                        <th >{t("allUsersWithClaimsTranslations.table.name")}</th>
                        <th>{t("allUsersWithClaimsTranslations.table.email")}</th>
                        <th className="text-center">{t("allUsersWithClaimsTranslations.table.actions")}</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedUsers.length === 0 ? (
                        <tr>
                            <td colSpan={3} className="text-center saas-no-data">
                                {t("allUsersWithClaimsTranslations.table.noUsersFound")}
                            </td>
                        </tr>
                    ) : (
                        paginatedUsers.map(user => (
                            <tr key={user.userId}>
                                <td className='bd'>{user.fullName || t("allUsersWithClaimsTranslations.table.noUsersFound")}</td>
                                <td>{user.email}</td>
                                <td className="text-center">
                                    <Button className="saas-btn me-2" size="sm" variant="primary" onClick={() => openPopup(user)}>
                                        {t("allUsersWithClaimsTranslations.buttons.viewClaims")}
                                    </Button>
                                    <Button className="saas-btn me-2" size="sm" variant="warning" onClick={() => handleImpersonate(user.userId)}>
                                        {t("allUsersWithClaimsTranslations.buttons.loginAsUser")}
                                    </Button>
                                    <Button className="saas-btn saas-btn-danger" size="sm" onClick={() => {
                                        setUserToDelete(user.userId);
                                        setDeleteSuccess(false);
                                        setDeleteError(null);
                                        setShowDeleteModal(true);
                                    }}>
                                        <i className="fas fa-trash me-2"></i> {t("allUsersWithClaimsTranslations.buttons.deleteUser")}
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {/* {totalPages > 1 && (
                <Pagination className="justify-content-center mt-3 saas-pagination">
                    <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                </Pagination>
            )} */}
            {totalPages > 1 && (
                <Pagination className="justify-content-center mt-3">
                    <Pagination.Prev
                        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        {t('claimModalsTranslations.pagination.previous')}
                    </Pagination.Prev>
                    {renderPaginationItems()}
                    <Pagination.Next
                        onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        {t('claimModalsTranslations.pagination.next')}
                    </Pagination.Next>
                </Pagination>
            )}

            <SingleUserAllClaims user={selectedUser} show={showPopup} onClose={closePopup} />
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("allUsersWithClaimsTranslations.modal.confirmDeleteTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {deleteSuccess ? (
                        <>
                            <div className="mb-3">
                                <i className="fas fa-check-circle fa-3x text-success"></i>
                                <img style={{ height: "70px", width: "70px" }} src={trashIcon} alt="" />
                            </div>
                            <h5 className="text-success">{t("allUsersWithClaimsTranslations.modal.deleteSuccessTitle")}</h5>
                            <p className="text-muted">{t("allUsersWithClaimsTranslations.modal.deleteSuccessDescription")}</p>
                        </>
                    ) : (
                        <>
                            <div className="mb-3"><i className="fas fa-exclamation-triangle fa-3x text-danger"></i> <img style={{ height: "70px", width: "70px" }} src={deleteIcon} alt="" /></div>
                            <p className="text-muted">
                                {t("allUsersWithClaimsTranslations.modal.deleteWarning")} <br />
                                <strong className="text-danger">{t("allUsersWithClaimsTranslations.modal.deleteWarningImportant")}</strong>
                            </p>
                            {deleteError && <p className="text-danger mt-2">{deleteError}</p>}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    {deleteSuccess ? (
                        <Button variant="success" onClick={() => setShowDeleteModal(false)}>
                            {t("allUsersWithClaimsTranslations.buttons.close")}
                        </Button>
                    ) : (
                        <>
                            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                                {t("allUsersWithClaimsTranslations.buttons.cancel")}
                            </Button>
                            <Button variant="danger" onClick={confirmDeleteUser} disabled={deleteLoading}>
                                {deleteLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-trash me-2"></i>
                                        {t("allUsersWithClaimsTranslations.buttons.confirmDelete")}
                                    </>
                                )}
                            </Button>


                        </>
                    )}
                </Modal.Footer>
            </Modal>

        </Card>

    );
};
